import { Close } from "@mui/icons-material";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { useState } from "react";

import { formatCurrencyToShow } from "../../helpers/currency";
import { useUpdateWithdrawalStatus } from "../../hooks/useUpdateWithdrawalStatus";
import { Withdrawal } from "../../interfaces/withdrawal";
import { DateTimeLabel } from "../DateTimeLabel";
import { PixTypeLabel } from "../PixTypeLabel";
import { WithdrawalApproveConfirmModal } from "../WithdrawalApproveConfirmModal";
import { WithdrawalRejectConfirmModal } from "../WithdrawalRejectConfirmModal";
import { WithdrawalStatusLabel } from "../WithdrawalStatusLabel";
import { WithdrawalTypeLabel } from "../WithdrawalTypeLabel";
import { DetailRow } from "./DetailRow";
import { DetailContainer } from "./styles";

interface WithdrawalAnalysisModalProps {
    open: boolean;
    onClose: () => void;
    data: Withdrawal | null;
    onSuccess?: (data: Withdrawal | null) => void;
    onError?: (data: Withdrawal | null, error?: string) => void;
}

export function WithdrawalAnalysisModal({
    open,
    onClose,
    data,
    onSuccess,
    onError,
}: WithdrawalAnalysisModalProps) {
    if (!open) return null;

    const [openChangeAmountModal, setOpenChangeAmountModal] = useState(false);
    const [openRejectModal, setOpenRejectModal] = useState(false);

    const { isSubmitting, mutate } = useUpdateWithdrawalStatus<null>({
        onSuccess,
        onError,
        onFinally: onClose,
    });

    const handleApproveConfirm = (withdrawal: Withdrawal) => {
        if (withdrawal?.type === "manager") {
            setOpenChangeAmountModal(true);
        } else {
            mutate(withdrawal.id, "approve", withdrawal);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
                <DialogTitle variant="h6" component="h2">
                    Solicitação de saque
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                {data?.status === "failed" && (
                    <Stack direction="row" gap={1} px={3} color="red">
                        <AnnouncementIcon />
                        <Typography>
                            Falha na comunicação com o serviço externo. Tente
                            novamente mais tarde
                        </Typography>
                    </Stack>
                )}

                <DialogContent
                    sx={{
                        pt: 2,
                        pb: 3,
                        mt: 1,
                        gap: 2,
                        flex: 1,
                        display: "flex",
                    }}
                >
                    {!!data?.bank_account && (
                        <DetailContainer>
                            {data?.bank_account?.split(",")?.map((item) => {
                                const l = item.split(":")[0]?.trim();
                                const v = item.split(":")[1]?.trim();
                                return (
                                    <DetailRow key={l} label={l} value={v} />
                                );
                            })}
                        </DetailContainer>
                    )}

                    <DetailContainer>
                        <DetailRow
                            label="Data da solicitação"
                            value={<DateTimeLabel value={data?.created_at} />}
                        />
                        <DetailRow
                            label="Solicitante"
                            value={<WithdrawalTypeLabel value={data?.type} />}
                        />
                        <DetailRow
                            label="Destinatário"
                            value={data?.recipient_name}
                        />
                        <DetailRow
                            label="Telefone"
                            value={data?.recipient_phone}
                        />
                        {data?.pix_key && (
                            <DetailRow
                                label="Tipo da chave PIX"
                                value={<PixTypeLabel value={data.pix_type} />}
                            />
                        )}
                        <DetailRow
                            label="Chave PIX"
                            value={data?.pix_key || "-"}
                        />
                        <DetailRow
                            label="Valor"
                            value={
                                <Typography variant="body2" fontWeight={600}>
                                    R${formatCurrencyToShow(data?.amount || 0)}
                                </Typography>
                            }
                        />
                        <DetailRow
                            label="Status"
                            value={
                                <WithdrawalStatusLabel value={data?.status} />
                            }
                        />
                    </DetailContainer>
                </DialogContent>

                <DialogContent sx={{ pt: 0 }} hidden={!data?.admin_note}>
                    <DetailContainer>
                        <DetailRow
                            label="Observação (Admin):"
                            value={data?.admin_note || "Nenhuma observação"}
                        />
                    </DetailContainer>
                </DialogContent>

                {["pending", "failed"].includes(data?.status || "") &&
                    data?.id && (
                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                variant="outlined"
                                color="error"
                                fullWidth
                                onClick={() => setOpenRejectModal(true)}
                                loading={isSubmitting === "reject"}
                            >
                                Recusar
                            </LoadingButton>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={() => handleApproveConfirm(data)}
                                loading={isSubmitting === "approve"}
                            >
                                Aprovar
                            </LoadingButton>
                        </DialogActions>
                    )}
            </Dialog>

            <WithdrawalApproveConfirmModal
                open={openChangeAmountModal}
                data={data}
                onCancel={() => setOpenChangeAmountModal(false)}
                onConfim={(d) => mutate(d.id, "approve", d)}
            />

            <WithdrawalRejectConfirmModal
                open={openRejectModal}
                data={data}
                onCancel={() => setOpenRejectModal(false)}
                onConfirm={(d) => mutate(d.id, "reject", d)}
            />
        </>
    );
}
